// RUBRIC COPY (Phase R3) — Appendix A of CVCRAFT_RUBRIC_CODEBOOK_v1.md, pinned as constants keyed by copy-string id.
//
// WHY A MODULE AND NOT STRINGS AT THE CALL SITE. Appendix A's contract (T-COPY-1) is that *every* rendered string
// resolves to an id here, and that the registered id set is computable from Appendix A alone — "any id in Appendix A
// that does not expand to a closed, enumerated set fails the contract". A single exported record keyed by id makes
// both halves mechanical: the copy tests pin the values, and `COPY_STRING_IDS` is the enumerable id set.
//
// THE §12.6 ALLOWLIST IS KEYED BY ID, NOT BY FREE TEXT. `CS-INTENDED-USE` and `CS-INTENDED-USE-SHORT` are the ONLY
// permitted occurrences of "candidate score" and "match outcomes" in the product (§1.3, §12.6 part 2). That is why
// they live here under an id rather than being written inline anywhere: the banned-output test allowlists the id.
//
// MARKDOWN IS NOT PART OF THE COPY. The codebook renders the lead sentence of each flag string in bold (`**…**`).
// That is emphasis in the specification document, not characters in the user-facing string, so the values below are
// the plain text. Where a string is a TEMPLATE, its slots are written `{name}` exactly as the codebook writes them.

/** §1.4 — the declared input surface. The review changes what it SAYS, not how it BANDS, based on this. */
export type InputSurface = 'custom_cv' | 'carms_my_information' | 'other_resume';

/* ------------------------------------------------------------------------------------------------------------ *
 * The pinned strings
 * ------------------------------------------------------------------------------------------------------------ */

/* ================================================================================================================
 * Appendix A.5-P — THE PROVISIONAL R5.3 SURFACE STRINGS, as their own const.
 *
 * DECLARED SEPARATELY AND SPREAD INTO `COPY`, deliberately. The ratified A.5 block inside `COPY` says "exactly
 * 28 rows", and that count has to stay independently checkable — so the provisional additions live in their own
 * object whose keys ARE the provisional id set (`PROVISIONAL_COPY_STRING_IDS`), and `COPY` is their union with
 * the ratified rows. Nothing about the 28 changes; the id set the surface may render grows.
 *
 * STATUS: **Claude+Sol adjudicated 2026-08-21 — 170 accepted as drafted, 5 replaced per Sol; owner may amend.**
 * The five replaced are the four A.5-P.5 extraction refusals and
 * `CS-VAL-RECOGNITION-TYPE-COMPETITIVE-SELECTION`; each carries its own reason at its own row.
 *
 * THE INVENTORY IS **194**, and its composition is pinned EXACTLY by `factLabels.test.ts` rather than described
 * here — 3 correction controls + 1 confirmation chip + 4 validation refusals + 3 announced waits + 5 extraction
 * refusals + 6 typed-value renderers (3 single-slot templates, 3 generic value labels) + 63 field labels (42 §2
 * fields, 21 §2.2a signals) + 90 closed-enum member labels + 11 layer section headings + 1 confirmation-screen
 * intro + 1 overlay recall disclosure (A.5-P.11) + 6 R5.7 read-surface usability strings (A.5-P.12). By id prefix
 * that is 63 `CS-FIELD-*`, 93 `CS-VAL-*` (the 90 enum members plus the three generic Yes/No/Not-stated labels,
 * which share the prefix without belonging to any enum), 3 `CS-VALUE-*`, 11 `CS-SECTION-*`, and 24 others.
 * Stating a count in a comment is how a count goes stale; the test is what keeps this one honest.
 *
 * THE LAST TWELVE CAME FROM THE ROUND-3 ACCESSIBILITY AUDIT (A.5-P.9 and A.5-P.10), not from the Claude+Sol copy
 * pass: six of nine layers were nameless landmarks with no heading between them, and the confirmation screen was
 * indistinguishable from Layer 1 of the read. A.5-P.11 (`CS-OVERLAY-RECALL-DISCLOSURE`) was added 2026-08-21,
 * Claude+Sol adjudicated, per Sol's requirement that every specialty-overlay prevalence label carry a recall
 * disclosure adjacent to it.
 * ============================================================================================================== */

/* ============================================================================================================== *
 * Appendix A.6 — THE R7 §6b SPECIALTY CONVENTION LAYER STRINGS (registered by R7.1).
 *
 * RATIFIED 2026-08-22 with codebook §6b; the strings below are the codebook's Appendix A.6 table VERBATIM, which
 * is itself `CVCRAFT_R7_SPEC_specialty_convention_layer.md` §4 verbatim. Not provisional: unlike A.5-P these were
 * owner-ratified before any code existed, and R7.1 is only the slice that registers them here.
 *
 * `CS-CONV-CARD-NOTDOC-SCOPE` IS DELIBERATELY ABSENT (spec §4, Sol edit c): a Not-documented card carries NO entry
 * count, and the Form 2 scope behind it surfaces only through the existing `CS-OMISSION-SCOPE` inside "show source".
 *
 * §12.6 ALLOWLIST. `CS-CONV-CLAIM`, the three hedges, `CS-CONV-NOT-A-WEIGHT` and the three `CS-CONV-EMPTY-*` rows
 * are the ONLY permitted occurrences of "prediction", "weight", "requirement" and "select" on this surface, and
 * only in negated form. `CONVENTION_ALLOWLISTED_COPY_IDS` below is that list, consulted by the §6b banned scan.
 * ============================================================================================================== */

const A6_COPY = {
  'CS-CONV-CLAIM':
    'These areas are described in cited Canadian specialty training and competency standards. This shows where '
    + 'your CV explicitly documents them. They are not program requirements, selection weights, or a prediction.',
  'CS-CONV-HEDGE-CONVENTION':
    'Source: a national training or competency standard for this specialty. Standards describe what training '
    + 'covers, not how programs select.',
  'CS-CONV-HEDGE-SURVEY':
    "Source: a published Canadian survey of program directors. It reports respondents' answers; it does not "
    + 'establish a weight, requirement, or prediction.',
  'CS-CONV-HEDGE-CORPUS':
    'Source: published program descriptions, counted by institution. A lower bound over what automated reading '
    + 'found — absence is not evidence that programs do not consider it.',
  'CS-CONV-CARD-DOC': 'Your CV documents this on {n} entries.',
  'CS-CONV-CARD-PARTIAL':
    'Your CV describes activity in this area but does not state what you were responsible for.',
  'CS-CONV-CARD-NOTDOC':
    'Your CV does not document this area. That may be because it is not described, or not part of your path.',
  'CS-CONV-RECOVER': '{recover_question}',
  'CS-CONV-UNBOUND':
    "This version cannot assess this area from your CV's recorded facts. It is listed so you can check it "
    + 'yourself.',
  'CS-CONV-SOURCE': 'Source: {source}, {pinpoint} ({date}).',
  'CS-CONV-SURVEY-CITE': 'Reported in: {citation} ({year}).',
  'CS-CONV-CORPUS-NOTE': "Named by {n} of {N} institutions' published descriptions for this specialty.",
  'CS-CONV-EMPTY-CONVENTION':
    'Convention areas for this specialty have not been reviewed for display in this release. The general review '
    + 'above applies.',
  'CS-CONV-EMPTY-SURVEY':
    'No verified Canadian program-director survey is on file for this specialty in this release.',
  'CS-CONV-EMPTY-CORPUS': 'Published-description counts are not shown for this specialty in this release.',
  'CS-CONV-NOT-A-WEIGHT':
    'Not a requirement, not a weight, not a prediction. Programs decide using the full application and their own '
    + 'criteria.',
  'CS-SECTION-CONVENTION': 'Specialty convention',
  'CS-SECTION-CONV-SURVEY': 'Published surveys',
  'CS-SECTION-CONV-CORPUS': 'Program descriptions (lower bound)',
} as const;

const A5P_COPY = {
  /* ============================================================================================================ *
   * Appendix A.5-P — THE PROVISIONAL R5.3 SURFACE STRINGS
   *
   * STATUS: **Claude+Sol adjudicated 2026-08-21 — 170 accepted as drafted, 5 replaced per Sol; owner may amend.**
   * These ids are NOT part of the ratified "exactly 28" A.5 block in `COPY` below, which is left byte-identical.
   * They are registered under the owner's standing rule for completing already-approved scope, and every one of
   * them is in the owner digest.
   *
   * WHY THEY EXIST. R5.3's build review found four places where the surface was rendering NOTHING because
   * Appendix A registered no words for it, and ruled that silence is not an acceptable resolution:
   *
   *   1. §5's `confirm` correction class had no control (only `CS-FLAG-WRONG-UNDO` reached it, sideways).
   *   2. §4.1 requires each fact's TYPED VALUE. A fact whose value is a date, a count, a boolean or a closed
   *      enum has no applicant prose to fall back on, so with no registered field label and no registered enum
   *      value label it could not render at all — and codebook §2.1 rule 5 ("the user can edit any fact") could
   *      not be honoured either, because an unlabelled field cannot be offered an editor.
   *   3. Validation refusals were marked `aria-invalid` and worded nowhere.
   *   4. The loading regions were announced (`role="status"`) and empty, and every extraction refusal collapsed
   *      into `CS-ERR-READ-UNAVAILABLE` — whose "Try again in a moment" is FALSE for a daily budget halt.
   *
   * TONE RULES ARE THE CODEBOOK'S, UNCHANGED: neutral register, no evaluative or comparative adjective about the
   * applicant, no §12 banned term, no timing promise that is not documented behaviour. Every label below names a
   * FIELD OF THE RECORD or a STATE OF THE REVIEW; none characterizes the person.
   * ============================================================================================================ */

  /* ---- A.5-P.9 — THE LAYER SECTION HEADINGS (accessibility audit A11Y-1, round 3). --------------------------
   *
   * WHAT THE AUDIT FOUND. §4's nine layers each render as their own `<section>`, and only three of them carried
   * an accessible name — the three that happened to have a registered line already heading them (the intended-use
   * statement, the facet framing sentence, the overlay hedge). The other six were NAMELESS LANDMARKS, and the
   * heading outline jumped straight from the intended-use H2 to entry-title and domain-name H3s. A screen-reader
   * user navigating this surface by landmark or by heading — which is how a long page IS navigated — could not
   * tell which layer they were in, only that they were somewhere in a long list of bands.
   *
   * SO EVERY LAYER GETS A REGISTERED NAME, and each is spoken through the same mechanism the three named ones
   * already used: a real `<h2>` carrying registered copy, referenced by `aria-labelledby`. NOT an `aria-label` —
   * T-COPY-1 does not stop applying to a string because it is only spoken, and a label attribute is a rendered
   * string with nowhere to declare its id.
   *
   * VISUALLY HIDDEN, and that is a deliberate scope decision rather than a shortcut. These headings exist to give
   * the ACCESSIBILITY TREE a structure it was missing; the visual layout is owner-frozen, and adding nine visible
   * headings to it would be a redesign nobody asked for, shipped inside an accessibility fix. `IntendedUse`
   * established exactly this pattern in R5.2 and it is followed here rather than invented.
   *
   * TONE: neutral NOUN PHRASES naming what the section contains — a part of the record, or a part of the reading
   * of it. None characterizes the applicant, none carries a band, a count or a comparative, and none is a
   * sentence, because a landmark name is a name.
   *
   * WHY THESE ARE NOT `aria-label`-SHAPED DUPLICATES OF EXISTING LINES. Where a section already had a registered
   * heading of its own — `Recommendations`' `CS-REC-GROUP` H2 — no new string is registered and none is needed.
   * The set below is exactly the sections that had none.
   */
  'CS-SECTION-LEDGER': 'Facts read from your CV',
  'CS-SECTION-FACETS': 'Entry by entry',
  'CS-SECTION-DOMAINS': 'Areas of activity',
  'CS-SECTION-DESCRIPTORS': 'Patterns across entries',
  'CS-SECTION-OVERLAY': 'Specialty criteria',
  'CS-SECTION-PRESENTATION': 'How the CV is set out',
  'CS-SECTION-FLAGS': 'Points to check',
  'CS-SECTION-CONFIDENCE': 'How complete this reading is',
  'CS-SECTION-CHANGES': 'What changed',
  'CS-SECTION-CONFIRM': 'Check the facts before the review',
  'CS-SECTION-LEGACY': 'Your previous-format review',

  /* ---- A.5-P.10 — THE CONFIRMATION SCREEN SAYS WHAT IT IS (accessibility audit A11Y-2, round 3). ------------
   *
   * WHAT THE AUDIT FOUND. §3's confirmation screen and Layer 1 of the read are the SAME component in two modes,
   * and to a screen-reader user they were indistinguishable: no heading, no intro, nothing announced on arrival.
   * The applicant was on a pre-review checking step, being asked to verify facts a paid review would be computed
   * from, and the only clue was a button reading "Looks right — run the review" — which explains what the button
   * does, not where they are or why they are being asked.
   *
   * ONE STRING, TWO JOBS, NO STUTTER. `CS-SECTION-CONFIRM` above is the screen's landmark NAME (a short noun
   * phrase, as a name should be); THIS is the screen's intro, rendered visibly at its top inside a polite live
   * region, so it both explains the step to a sighted reader and is announced on arrival to a screen-reader user.
   * The audit proposed this sentence as the H2 itself; it is a sentence, and a sentence makes a poor landmark
   * name, so the two roles are split rather than one string being made to do both badly.
   *
   * IT PROMISES NOTHING THE SCREEN DOES NOT DO. "Edit or flag anything that is wrong" is exactly the two
   * correction classes the panel offers here, and "then run the review" names the proceed control's own effect.
   * It does NOT say the facts will be confirmed by proceeding — they will not: §3's skip writes nothing, and
   * anything the applicant did not act on stays `extracted`.
   */
  'CS-CONFIRM-SCREEN-INTRO':
    'Check the facts we read from your CV. Edit or flag anything that is wrong, then run the review.',

  /* ---- A.5-P.1 — the correction controls §5 named and Appendix A had no label for. ---------------------------
   *
   * `CS-CONFIRM-ENTRY-ACTION` IS ENTRY-SCOPED, and deliberately so: `confirmation_state` is a column on
   * `fact_ledger_entries` (§2.2), the PATCH route accepts `confirmation_state` for an ENTRY and nothing smaller,
   * and a per-FACT control that silently confirmed the whole entry would be the same false affordance the build
   * review rejected for evidence provenance. The label says which scope it acts on.
   */
  'CS-CONFIRM-ENTRY-ACTION': 'Confirm this entry',
  'CS-EDIT-SAVE': 'Save',
  'CS-EDIT-CANCEL': 'Cancel',

  /* ---- A.5-P.2 — §2.8's FIFTH confirmation state, which A.5 registered no chip for. -------------------------- */
  'CS-FACT-DECLINED': 'Left unconfirmed',

  /* ---- A.5-P.3 — validation, worded. Each names what is wrong and what to do about it, and nothing else. -----
   *
   * ONE STRING PER REACHABLE REFUSAL REASON, not one generic sentence: the three reasons have three different
   * remedies, and the `rejected` correction state already carries which one the server reported.
   */
  'CS-EDIT-INVALID-CHARACTERS': "This value contains characters we can't store. Remove them and save again.",
  'CS-EDIT-INVALID-VALUE': "This value isn't one this field accepts. Change it and save again.",
  'CS-EDIT-TOO-LONG': 'This value is longer than this field accepts. Shorten it and save again.',
  'CS-EDIT-REQUIRED': 'This field needs a value. Enter one, or choose {not_stated}.',

  /* ---- A.5-P.4 — the announced waits. An empty `role="status"` announces nothing. ---------------------------- */
  'CS-READ-LOADING': 'Loading your review.',
  'CS-EXTRACTION-LOADING': 'Reading your CV to build your review.',
  'CS-LEGACY-LOADING': 'Loading your previous-format review.',

  /* ---- A.5-P.5 — the extraction refusals, SPLIT BY WHAT IS ACTUALLY TRUE OF EACH. ----------------------------
   *
   * `CS-ERR-READ-UNAVAILABLE` ends "Try again in a moment", which is accurate for a transport failure and FALSE
   * for a daily cap: retrying in a moment fails again, all day. So the halts do not offer an immediate retry, and
   * the superseded/conflict string offers the recovery that actually exists — re-read, not retry — because a
   * second attempt is refused by the commit fence by design.
   *
   * ALL FOUR REWORDED IN ROUND 3 (finding 5), because the round-2 drafts each promised something the server does
   * not actually guarantee. Every replacement is Sol's, verbatim:
   *
   *   * IN-PROGRESS  — "This one can start once that finishes" promised that THIS CV would become startable when
   *     the other attempt ended. The lease is ACCOUNT-level and the other attempt may be for a different CV, so
   *     the only thing we can honestly tell the applicant is to wait and re-read the state.
   *   * FREE-CAPACITY — "again tomorrow" is false whenever the shared `free_llm_at_capacity` is the PERSISTENT
   *     kill switch (`llmBudget.ts`) rather than the daily counter. One error, two causes, one of which has no
   *     tomorrow; the string now names neither.
   *   * ACCOUNT-LIMIT — the cap resets on a UTC day boundary, so "tomorrow" is wrong for anyone whose local
   *     calendar has already turned. It states the documented reset instead of a relative day.
   *   * SUPERSEDED — one string covers `extraction_conflict` AND `extraction_attempt_superseded`, and in neither
   *     case need another attempt have FINISHED, nor concern the same CV. What is true of both is that the review
   *     state moved while this CV was being read.
   */
  'CS-EXTRACT-IN-PROGRESS':
    'A review is already being prepared for your account. Wait for it to finish, then reload this page.',
  'CS-EXTRACT-CAPACITY': "We couldn't read your CV just now. You can try again.",
  'CS-EXTRACT-FREE-CAPACITY': 'Free reviews are unavailable right now. Try again later.',
  'CS-EXTRACT-ACCOUNT-LIMIT':
    'This account has reached its daily review limit. The limit resets at 00:00 UTC.',
  'CS-EXTRACT-SUPERSEDED':
    'The review state changed while this CV was being read. Reload this page to check the current status.',

  /* ---- A.5-P.6 — typed-value renderers for the values that are NOT prose. ------------------------------------
   *
   * A date-partial and a stated count are DATA, not sentences, and there is no applicant clause to quote for
   * them: §2.2 stores `date_start` as `YYYY`/`YYYY-MM` and `team_size` as an integer. They render inside a
   * registered string whose only content is the slot, so the rendered text still resolves to an Appendix A id
   * (T-COPY-1) and the value is still scanned as system-authored text (T-BAN-2s) rather than claiming an
   * `applicant_verbatim` exemption it has not earned — the stored value is a NORMALIZATION of what the applicant
   * wrote, not the words themselves.
   */
  'CS-VALUE-DATE': '{date}',
  'CS-VALUE-COUNT': '{count}',
  /* The same construction for a text-kind value whose provenance is `derived` or `closed_world` — a string this
   * SYSTEM computed from other facts, not one the applicant wrote. It renders through a registered template and
   * is scanned as system copy, rather than through `ApplicantText`, which would claim a provenance it lacks. */
  'CS-VALUE-TEXT': '{text}',
  'CS-VAL-YES': 'Yes',
  'CS-VAL-NO': 'No',
  'CS-VAL-NOT-STATED': 'Not stated',

  /* ---- A.5-P.7 — FIELD LABELS (§2.2, §2.2a, §2.4, §2.5, §2.6). ----------------------------------------------
   *
   * One id per field name the ledger can carry a fact for. The set is CLOSED and drift-checked: `factLabels.ts`
   * asserts that every member of the extractor's own `SPANNABLE_FIELD_NAMES`, of `SIGNAL_TYPES`, and of
   * `EDITABLE_FIELDS` resolves here, so a field added to extraction without a label fails a test rather than
   * silently disappearing from Layer 1.
   */
  'CS-FIELD-ENTRY-TYPE': 'Entry type',
  'CS-FIELD-DATE-START': 'Start date',
  'CS-FIELD-DATE-END': 'End date',
  'CS-FIELD-IS-ONGOING': 'Ongoing',
  'CS-FIELD-DURATION-STATED': 'Duration, as written',
  'CS-FIELD-FREQUENCY': 'How often',
  'CS-FIELD-ROLE-TITLE': 'Role title',
  'CS-FIELD-RESPONSIBILITY-STATEMENT': 'Stated responsibility',
  'CS-FIELD-SUPERVISION-LEVEL': 'Supervision, as stated',
  'CS-FIELD-JURISDICTION': 'Country or province',
  'CS-FIELD-TRAINING-STAGE': 'Training stage at the time',
  'CS-FIELD-TEAM-SIZE': 'Team size, as stated',
  'CS-FIELD-INSTITUTION': 'Institution',
  'CS-FIELD-ACTIVITY-STATUS': 'Activity status',
  'CS-FIELD-SOURCE-SECTION': 'Section of your CV',
  'CS-FIELD-SECTION-HEADING': 'Section heading',
  'CS-FIELD-RAW-TEXT': 'Entry text',
  'CS-FIELD-ORGANIZATION-STATED': 'Organization, as stated',
  /* §2.2/§4.2/§11.2 — the OPAQUE distinctness token. Registered so the field has a name in the digest; it never
   * renders a value, because the token carries no name, no geography, no tier and no ordering by ratification. */
  'CS-FIELD-ORGANIZATION-CONTEXT': 'Organization grouping',
  'CS-FIELD-TOPIC-TAG': 'Topic',

  /* §2.2 activity-status wording evidence (the `signals.*` family — distinct from §2.2a's `signal.*`). */
  'CS-FIELD-COMPLETION-CLAUSE': 'Completion wording',
  'CS-FIELD-CONTINUATION-CLAUSE': 'Continuation wording',
  'CS-FIELD-PAST-TENSE-EVIDENCE': 'Past-tense wording',
  'CS-FIELD-CONTINUING-PRESENT-EVIDENCE': 'Present-tense wording',

  /* §2.4 scholarly work. */
  'CS-FIELD-WORK-TYPE': 'Type of work',
  'CS-FIELD-WORK-TITLE': 'Work title',
  'CS-FIELD-AUTHORSHIP-POSITION': 'Authorship position',
  'CS-FIELD-AUTHOR-COUNT': 'Number of authors',
  'CS-FIELD-AUTHOR-ORDER-CONVENTION': 'Author-order convention',
  'CS-FIELD-CONTRIBUTION-STATEMENT': 'Stated contribution',
  'CS-FIELD-RESULT-DIRECTION': 'Stated result direction',

  /* §2.5 recognition. */
  'CS-FIELD-RECOGNITION-TYPE': 'Type of recognition',
  'CS-FIELD-AWARD-SCOPE': 'Scope of the award',
  'CS-FIELD-SELECTION-BASIS': 'Selection basis, as stated',
  'CS-FIELD-MONETARY-VALUE': 'Monetary value, as stated',
  'CS-FIELD-RECOGNITION-YEAR': 'Year',

  /* §2.6 clinical. */
  'CS-FIELD-CLINICAL-SETTING': 'Clinical setting',
  'CS-FIELD-CLINICAL-DISCIPLINE': 'Discipline',
  'CS-FIELD-EXPOSURE-TYPE': 'Type of clinical exposure',
  'CS-FIELD-WEEKS-STATED': 'Weeks, as stated',
  'CS-FIELD-PATIENT-CONTACT': 'Patient contact, as stated',
  'CS-FIELD-LICENCE-STATUS': 'Licence status at the time',

  /* §2.2a atomic signal facts — one label per registered `signal_type`, all 20. Each says what the CV STATES,
   * never what the applicant is: a signal is a reading of the document, and the label has to read as one. */
  'CS-FIELD-SIGNAL-PARTICIPATORY-FUNCTION': 'States taking part in the work',
  'CS-FIELD-SIGNAL-BOUNDED-OBJECT': 'States a specific thing worked on',
  'CS-FIELD-SIGNAL-OWNERSHIP-RELATION': 'States responsibility for that thing',
  'CS-FIELD-SIGNAL-DIRECTION-OF-WHOLE': 'States directing the whole activity',
  'CS-FIELD-SIGNAL-COORDINATION-OF-OTHERS': 'States coordinating other people',
  'CS-FIELD-SIGNAL-STATED-INDEPENDENT-PRACTICE': 'States practising independently',
  'CS-FIELD-SIGNAL-OUTCOME-OR-RECOGNITION': 'States an outcome or recognition',
  'CS-FIELD-SIGNAL-D3-NAMED-SETTING': 'Names a setting',
  'CS-FIELD-SIGNAL-D3-STATED-COUNT': 'States a count',
  'CS-FIELD-SIGNAL-D3-NAMED-DELIVERABLE': 'Names a deliverable',
  'CS-FIELD-SIGNAL-D3-NAMED-AUDIENCE': 'Names an audience',
  'CS-FIELD-SIGNAL-D3-NAMED-COLLABORATING-GROUP': 'Names a collaborating group',
  'CS-FIELD-SIGNAL-DESCRIBES-ACTIVITY': 'Describes an activity',
  'CS-FIELD-SIGNAL-COLLABORATION-FUNCTION': 'States a collaborative function',
  'CS-FIELD-SIGNAL-ORGANIZATION-CONTEXT': 'Names an organization, program, or project',
  'CS-FIELD-SIGNAL-STATED-PROGRESSION': 'States a change in role over time',
  /* The §4.3 domain-assignment family. Each ENDS with the domain's own ratified A.2 name, and `rubricCopy.test`
   * asserts exactly that, so the two cannot drift apart. */
  'CS-FIELD-SIGNAL-DOMAIN-CLINICAL': 'Assigned to Clinical experience & documented responsibility',
  'CS-FIELD-SIGNAL-DOMAIN-LEADERSHIP': 'Assigned to Leadership & project ownership',
  'CS-FIELD-SIGNAL-DOMAIN-SCHOLARSHIP': 'Assigned to Scholarship, inquiry & QI',
  'CS-FIELD-SIGNAL-DOMAIN-SERVICE': 'Assigned to Service & advocacy',
  'CS-FIELD-SIGNAL-DOMAIN-TEACHING': 'Assigned to Teaching & mentorship',

  /* ---- A.5-P.8 — ENUM VALUE LABELS. One id per member of every closed §2 enum a fact can carry. -------------
   *
   * These are what make "render the fact's typed value" possible at all: the stored value is an internal token
   * (`supervised_trainee_postgrad`), and §0/T-COPY-1 forbid printing internal vocabulary at the applicant. They
   * are also what makes §2.1 rule 5's "the user can edit any fact" reachable for an enum field, because a select
   * needs a label per option.
   */

  /* §2.2 `entry_type` (11). */
  'CS-VAL-ENTRY-TYPE-CLINICAL': 'Clinical',
  'CS-VAL-ENTRY-TYPE-RESEARCH-PROJECT': 'Research project',
  'CS-VAL-ENTRY-TYPE-SCHOLARLY-WORK': 'Scholarly work',
  'CS-VAL-ENTRY-TYPE-TEACHING': 'Teaching',
  'CS-VAL-ENTRY-TYPE-LEADERSHIP-ROLE': 'Leadership role',
  'CS-VAL-ENTRY-TYPE-SERVICE': 'Service',
  'CS-VAL-ENTRY-TYPE-EMPLOYMENT': 'Employment',
  'CS-VAL-ENTRY-TYPE-AWARD': 'Award',
  'CS-VAL-ENTRY-TYPE-CERTIFICATION': 'Certification',
  'CS-VAL-ENTRY-TYPE-MEMBERSHIP': 'Membership',
  // 2026-08-23 — the two `EntryType` members added with their own §2.2b profiles. Registered here so the value
  // renders on the facts screen and can be chosen in a correction, exactly like every sibling member.
  'CS-VAL-ENTRY-TYPE-EDUCATION': 'Education',
  'CS-VAL-ENTRY-TYPE-LANGUAGE': 'Language',
  'CS-VAL-ENTRY-TYPE-OTHER': 'Other',

  /* §2.2 `source_section` (11). The document's OWN label, never the domain assignment (§4.3). */
  'CS-VAL-SOURCE-SECTION-RESEARCH': 'Research',
  'CS-VAL-SOURCE-SECTION-PUBLICATIONS': 'Publications',
  'CS-VAL-SOURCE-SECTION-EDUCATION': 'Education',
  'CS-VAL-SOURCE-SECTION-WORK': 'Work',
  'CS-VAL-SOURCE-SECTION-VOLUNTEER': 'Volunteer',
  'CS-VAL-SOURCE-SECTION-LEADERSHIP': 'Leadership',
  'CS-VAL-SOURCE-SECTION-TEACHING': 'Teaching',
  'CS-VAL-SOURCE-SECTION-AWARDS': 'Awards',
  'CS-VAL-SOURCE-SECTION-PRESENTATIONS': 'Presentations',
  'CS-VAL-SOURCE-SECTION-OTHER': 'Other',
  'CS-VAL-SOURCE-SECTION-UNLABELLED': 'No section heading',

  /* §2.2 `frequency` (6). */
  'CS-VAL-FREQUENCY-ONE-OFF': 'One occasion',
  'CS-VAL-FREQUENCY-WEEKLY': 'Weekly',
  'CS-VAL-FREQUENCY-MONTHLY': 'Monthly',
  'CS-VAL-FREQUENCY-PER-TERM': 'Each term',
  'CS-VAL-FREQUENCY-IRREGULAR': 'Irregular',
  'CS-VAL-FREQUENCY-CONTINUOUS': 'Continuous',

  /* §2.2 / §3.5 `supervision_level` (5). RECORDED, NEVER ADJUDICATED — the labels describe what the CV states. */
  'CS-VAL-SUPERVISION-OBSERVER-SHADOWING': 'Observing or shadowing',
  'CS-VAL-SUPERVISION-SUPERVISED-STUDENT': 'Supervised, as a student',
  'CS-VAL-SUPERVISION-SUPERVISED-TRAINEE-POSTGRAD': 'Supervised, as a postgraduate trainee',
  'CS-VAL-SUPERVISION-STATED-INDEPENDENT': 'Stated as independent',
  'CS-VAL-SUPERVISION-NON-CLINICAL': 'Non-clinical',

  /* §2.2 `training_stage_at_time` (5). */
  'CS-VAL-TRAINING-STAGE-PRE-MED': 'Before medical school',
  'CS-VAL-TRAINING-STAGE-UNDERGRAD-MED': 'Undergraduate medical training',
  'CS-VAL-TRAINING-STAGE-POSTGRAD': 'Postgraduate training',
  'CS-VAL-TRAINING-STAGE-POST-LICENSURE': 'After licensure',
  'CS-VAL-TRAINING-STAGE-NON-MEDICAL': 'Non-medical',

  /* §2.2 `activity_status` (2 stated members; absence is `CS-VAL-NOT-STATED`, never "zero" or "lacking"). */
  'CS-VAL-ACTIVITY-STATUS-COMPLETED': 'Completed',
  'CS-VAL-ACTIVITY-STATUS-UNDER-WAY': 'Under way',

  /* §2.4 `work_type` (8). */
  'CS-VAL-WORK-TYPE-EMPIRICAL-STUDY': 'Empirical study',
  'CS-VAL-WORK-TYPE-REVIEW-SYNTHESIS': 'Review or synthesis',
  'CS-VAL-WORK-TYPE-CASE-REPORT': 'Case report',
  'CS-VAL-WORK-TYPE-QI-PROJECT': 'Quality-improvement project',
  'CS-VAL-WORK-TYPE-THESIS': 'Thesis',
  'CS-VAL-WORK-TYPE-EDUCATIONAL-MATERIAL': 'Educational material',
  'CS-VAL-WORK-TYPE-COMMENTARY-EDITORIAL': 'Commentary or editorial',
  'CS-VAL-WORK-TYPE-OTHER-SCHOLARLY': 'Other scholarly work',

  /* §2.4 `authorship_position` (7). §3.6 A: recorded as documentation, never a driver of any band. */
  'CS-VAL-AUTHORSHIP-FIRST': 'First author',
  'CS-VAL-AUTHORSHIP-CO-FIRST-STATED': 'Co-first author, as stated',
  'CS-VAL-AUTHORSHIP-MIDDLE': 'Middle author',
  'CS-VAL-AUTHORSHIP-LAST-SENIOR': 'Last author',
  'CS-VAL-AUTHORSHIP-CORRESPONDING-STATED': 'Corresponding author, as stated',
  'CS-VAL-AUTHORSHIP-GROUP-CONSORTIUM': 'Group or consortium author',
  'CS-VAL-AUTHORSHIP-POSITION-NOT-DETERMINABLE': 'Position not determinable from the citation',

  /* §2.4 `author_order_convention` (4). */
  'CS-VAL-AUTHOR-ORDER-CONTRIBUTION-ORDERED-ASSUMED': 'Contribution-ordered, assumed',
  'CS-VAL-AUTHOR-ORDER-ALPHABETICAL-APPARENT': 'Alphabetical, apparently',
  'CS-VAL-AUTHOR-ORDER-CONSORTIUM': 'Consortium',
  'CS-VAL-AUTHOR-ORDER-UNKNOWN': 'Convention not determinable',

  /* §2.4 `result_direction` (3 stated members). */
  'CS-VAL-RESULT-DIRECTION-POSITIVE': 'Positive',
  'CS-VAL-RESULT-DIRECTION-NULL-OR-NEGATIVE': 'Null or negative',
  'CS-VAL-RESULT-DIRECTION-MIXED': 'Mixed',

  /* §2.5 `recognition_type` (6). */
  'CS-VAL-RECOGNITION-TYPE-GRANT': 'Grant',
  'CS-VAL-RECOGNITION-TYPE-SCHOLARSHIP': 'Scholarship',
  'CS-VAL-RECOGNITION-TYPE-PRIZE-AWARD': 'Prize or award',
  'CS-VAL-RECOGNITION-TYPE-HONOUR-SOCIETY': 'Honour society',
  /* The §2.5 member is spelled `competitive_selection` INTERNALLY; its LABEL may not be, because "competitive" is
   * a §12.2 banned term and the registry governs what CVCraft says, not what its enums are called.
   *
   * REWORDED IN ROUND 3 (finding 5), Sol's replacement verbatim. "Selection process" named a PROCESS, and this
   * field is `recognition_type` — the answer to "what kind of recognition is this?", so a bare process name is
   * the wrong grammatical category and reads as though the entry itself were a process. "Recognition through a
   * selection process" names the recognition and how it was arrived at, still with no comparative claim in it. */
  'CS-VAL-RECOGNITION-TYPE-COMPETITIVE-SELECTION': 'Recognition through a selection process.',
  'CS-VAL-RECOGNITION-TYPE-NOT-DETERMINABLE': 'Type not determinable',

  /* §2.5 `award_scope` (5). DESCRIPTOR, NOT RANK (§2.5) — the labels name a scope and imply no ordering. */
  'CS-VAL-AWARD-SCOPE-COURSE-LEVEL': 'Course level',
  'CS-VAL-AWARD-SCOPE-INSTITUTIONAL': 'Institutional',
  'CS-VAL-AWARD-SCOPE-PROVINCIAL-REGIONAL': 'Provincial or regional',
  'CS-VAL-AWARD-SCOPE-NATIONAL': 'National',
  'CS-VAL-AWARD-SCOPE-INTERNATIONAL': 'International',

  /* §2.5 `selection_basis_stated` (4 stated members). */
  /* Same reason as `CS-VAL-RECOGNITION-TYPE-COMPETITIVE-SELECTION` above: the internal member is
   * `merit_competitive`, the label is not. "Merit-based" is what distinguishes it from `need_based`. */
  'CS-VAL-SELECTION-BASIS-MERIT-COMPETITIVE': 'Merit-based',
  'CS-VAL-SELECTION-BASIS-NEED-BASED': 'Need-based',
  'CS-VAL-SELECTION-BASIS-NOMINATION': 'Nomination',
  'CS-VAL-SELECTION-BASIS-PARTICIPATION': 'Participation',

  /* §2.6 `exposure_type` (7). */
  'CS-VAL-EXPOSURE-OBSERVERSHIP': 'Observership',
  'CS-VAL-EXPOSURE-ELECTIVE': 'Elective',
  'CS-VAL-EXPOSURE-CORE-ROTATION': 'Core rotation',
  'CS-VAL-EXPOSURE-SELECTIVE': 'Selective',
  'CS-VAL-EXPOSURE-EMPLOYMENT-CLINICAL': 'Clinical employment',
  'CS-VAL-EXPOSURE-VOLUNTEER-CLINICAL': 'Clinical volunteering',
  'CS-VAL-EXPOSURE-SIMULATION': 'Simulation',

  /* §2.6 `patient_contact_stated` (2 stated members). */
  'CS-VAL-PATIENT-CONTACT-STATED-DIRECT': 'Stated as direct',
  'CS-VAL-PATIENT-CONTACT-STATED-OBSERVATIONAL': 'Stated as observational',

  /* §2.6 `licence_status_at_time` (4 stated members). §3.5 S2: recorded, never adjudicated. */
  'CS-VAL-LICENCE-UNLICENSED': 'Unlicensed',
  'CS-VAL-LICENCE-STUDENT-REGISTRATION': 'Student registration',
  'CS-VAL-LICENCE-PROVISIONAL': 'Provisional licence',
  'CS-VAL-LICENCE-FULL': 'Full licence',

  /*
   * A.5-P.11 — the recall disclosure Sol required adjacent to every specialty-overlay prevalence label.
   *
   * Claude+Sol adjudicated 2026-08-21. Registered under the same owner standing rule as the rest of A.5-P
   * (completing already-approved scope; no deploy, no money, no destructive action, no scope expansion); in the
   * owner digest for amendment or rejection like every other A.5-P row.
   *
   * WHY IT EXISTS. `CS-OVERLAY-HEDGE` (§6.4, ratified A.5) says the criteria state what programs name, not how
   * they weigh it. It says nothing about RECALL — that the underlying detection is a lower bound over public
   * program descriptions, and can miss criteria or whole programs. Sol's finding: a prevalence label rendered
   * with the weighing hedge alone but no recall hedge lets a reader treat "not explicitly named" as "not valued",
   * and treat "most" as a true population estimate rather than a floor over what text search found. This string
   * closes that gap. It carries no number — the pilot's measured recall figure lives in the measurement report,
   * not in user-facing copy — and no §12-banned vocabulary.
   */
  'CS-OVERLAY-RECALL-DISCLOSURE':
    'These prevalence labels are based on criteria automatically found in public program descriptions and are ' +
    'lower bounds: some criteria and programs may be missed. "Most" and "some" describe what was found, not ' +
    'estimated true prevalence, and absence is not evidence that a program does not value a criterion.',

  /*
   * A.5-P.12 — THE R5.7 READ-SURFACE USABILITY STRINGS (live smoke 2026-08-22, Claude+Sol rulings).
   *
   * **Provisional — Claude+Sol adjudicated; owner may amend.** Registered under the same owner standing rule as
   * the rest of A.5-P (completing already-approved scope; no deploy, no money, no destructive action, no scope
   * expansion); every id is in the owner digest for amendment or rejection.
   *
   * WHY EACH EXISTS — every one names a STATE OF THE SURFACE or a PART OF THE RECORD, none characterizes the
   * applicant, none carries a band, a count or a comparative:
   *
   *   * `CS-OMISSION-SCOPE-ENTRY` — §4.9's questions are PER-ENTRY closed-world claims (`omissionScope` assesses
   *     exactly one entry: `entry_ids_assessed = [entry]`, `n_assessed = 1`). Rendering the ratified
   *     `CS-OMISSION-SCOPE` ("assessed across all {n} entries in your CV") for them said "all 1 entries in your
   *     CV" — a false scope. The per-entry scope gets its own honest sentence; the ratified string stays for
   *     genuine CV-wide/domain scopes (T-COUNT-1's sole allowlisted count is unchanged — this string carries NO
   *     number). The disclosure is not deleted (C2b); it is worded for the scope it actually has.
   *   * `CS-CONF-MODERATE-ENTRY` — the ratified `CS-CONF-MODERATE` ends "check the facts below", and Layer 1 (the
   *     facts) is ABOVE Layer 8 (confidence) in §4's fixed order. The ratified row is untouched (A.5 stays exactly
   *     28, byte-identical); this is the per-entry line's directional replacement, and the surface renders it ONLY
   *     on the per-entry confidence line, never for the review-level badge.
   *   * `CS-ENTRY-FACTS-LINK` — the in-page control beside a moderate/low per-entry confidence line that moves
   *     focus to that entry's Layer 1 card. A link needs a name; the name is registered.
   *   * `CS-ENTRY-EXPAND` / `CS-ENTRY-COLLAPSE` — Layer 1's per-entry accordion toggle (default-collapsed in the
   *     read; default-expanded on the confirmation screen). Each button's accessible name is this verb plus the
   *     entry's own label, visually hidden, so repeated toggles are never three buttons named "Show facts".
   *   * `CS-ENTRY-DATE-RANGE` — the entry label's date range, two `CS-VALUE-DATE`-shaped slots joined by a
   *     registered connective; a single known date still renders through `CS-VALUE-DATE` alone. The connective is
   *     registered because a bare " – " text node would be a rendered string belonging to no id (T-COPY-1).
   */
  'CS-OMISSION-SCOPE-ENTRY': 'Assessed within this entry.',
  'CS-CONF-MODERATE-ENTRY': "Some of this entry wasn't clear to read — check this entry's facts.",
  'CS-ENTRY-FACTS-LINK': "Go to this entry's facts",
  'CS-ENTRY-EXPAND': 'Show facts',
  'CS-ENTRY-COLLAPSE': 'Hide facts',
  'CS-ENTRY-DATE-RANGE': '{start} to {end}',

  /*
   * A.5-P.13 — THE R5.8 ENTRY-TYPE CALIBRATION STRING (spec `CVCRAFT_R5_8_SPEC_entry_type_calibration.md` rev 2,
   * Claude+Sol decisions 2026-08-22; provisional — owner may amend).
   *
   * `CS-FACET-NA` replaces a facet's L1 level label ONLY when the entry's expectation profile marks that facet
   * `not_expected` AND the raw level is L1 (`facetNotExpectedCopyId`, entryTypeProfiles.ts). It is FACET-NEUTRAL
   * on purpose — it does not say "title, setting, and dates", because the same string stands in for a not-expected
   * Completion or Continuity row — and it describes the DOCUMENT CONVENTION, never the applicant: no band, no
   * count, no comparative, no "lacks"/"missing" (§4.7, §8.5, §12.2). A stated level (≥ L2) always renders as itself.
   */
  'CS-FACET-NA': 'Not expected for this kind of entry — a CV conventionally does not state this here.',

  /*
   * A.5-P.14 — THE LEGACY-CHROME COPY THE R5.7b READ RE-USES (R5.7b fix, Claude+Sol adjudicated under owner
   * delegation 2026-08-22; provisional — owner may amend). The read renders through the legacy `QualityReadView`
   * presentation AS IS; three of its frozen chrome strings misdescribed the engine's NEUTRAL prompts (§8.3 F2: a
   * flag is a prompt, never an instruction to fix), so each is replaced by a registered string at the same slot —
   * a copy change only, no layout change:
   *   * `CS-LEGACY-ROW-ACTION` — the per-row disposition label that read "Sharpen" (an instruction derived from a
   *     failure detector). The engine's rows are clarification prompts and questions; "Clarify" names what the row
   *     asks for without asserting the point is weak.
   *   * `CS-POINT-TO-CHECK-ONE` / `CS-POINTS-TO-CHECK` — the collapsed-entry row's count that read "N points to
   *     review". Navigation cardinality (Sol ruled a simple count acceptable there); "to check" matches the
   *     registered `CS-SECTION-FLAGS` ("Points to check"). Never a fraction, never a total beside it.
   */
  'CS-LEGACY-ROW-ACTION': 'Clarify',
  'CS-POINT-TO-CHECK-ONE': '1 point to check',
  'CS-POINTS-TO-CHECK': '{n} points to check',

  /*
   * A.5-P.15 — THE REVIEW-LEVEL CONFIDENCE SENTENCES (live old-vs-new comparison 2026-08-22, Claude+Sol agree;
   * provisional — owner may amend). The legacy read's opening line (`overall`) carries the engine's
   * `review_confidence`; the ratified §9.2 `CS-CONF-*` rows are worded for ONE ENTRY ("We read this entry
   * clearly."), so rendering them as the CV-level lead misdescribed the scope. These three are the CV-level
   * equivalents, one per level, and the surface renders them ONLY in the legacy opening-line slot; the ratified rows
   * stay byte-identical and stay the per-entry line. `-MODERATE` / `-LOW` point at "the entries' facts" — the facts
   * screen (Layer 1) sits below the legacy body, so "below" is true here, but the directional phrase is kept
   * position-free like `CS-CONF-MODERATE-ENTRY`.
   */
  'CS-REVIEW-CONF-HIGH': 'We read your CV clearly.',
  'CS-REVIEW-CONF-MODERATE': "Some of your CV wasn't clear to read — check the entries' facts.",
  'CS-REVIEW-CONF-LOW': "Much of your CV wasn't clear to read — check the entries' facts.",

  /*
   * A.5-P.16 — THE L1 SLOT STRINGS OF THE RESTORED LEGACY FLOW (owner directive 2026-08-23: "copy it and maintain
   * as is; you can adjust the added contents but the FLOW should remain the same"; provisional — owner may amend).
   * The legacy overview is the CV's OWN SECTIONS as cards; the engine's domain bands, presentation bands and
   * review confidence ride in the legacy OPENING LINE (`overall`) as compact registered sentences, not as cards.
   *   * `CS-OVERVIEW-BAND-SENTENCE` — one "{name}: {band}." sentence per domain / presentation element; `{name}` is the
   *     registered `CS-DOMAIN-*` / `CS-SECTION-PRESENTATION` / `CS-PSUB-*` label and `{band}` the registered
   *     `CS-BAND-*` / `CS-PBAND-*` label (a Not-shown domain carries the §4.7 claim with its scope disclosure in
   *     the band slot, exactly as before). Both slots are registered text; the template adds punctuation only.
   *   * `CS-NOTHING-FLAGGED` — the section card's "Do this first" slot when no entry of that section carries a
   *     flag or a question. States the absence of prompts; claims nothing about the entries (§4.7, §12.2).
   */
  'CS-OVERVIEW-BAND-SENTENCE': '{name}: {band}.',
  'CS-NOTHING-FLAGGED': 'No points to check in this section’s entries.',
  /*
   * A.5-P.19 — `CS-NO-POINTS-TO-CHECK`, the SECTION INDEX's per-entry counterpart of `CS-POINTS-TO-CHECK`
   * (owner directive 2026-08-23: "maybe a table or some organizing format will help"; Sol round 2: the index row
   * must state the checkable fact, not interpret the entry as fine). It says exactly what the row's green bar
   * means — that no point of this entry was flagged — and claims nothing about the entry's quality (§4.7, §12.2).
   */
  'CS-NO-POINTS-TO-CHECK': 'No points to check',

  /*
   * A.5-P.17 — THE GROUPED "NOT SHOWN" SENTENCE OF THE OPENING LINE (Sol one-pass on the restored flow, 2026-08-23,
   * owner: original flow as is; provisional — owner may amend). A thin CV read five near-identical §4.7 claims in
   * a row; the Not-shown domains sharing ONE valid closed-world scope count now collapse into ONE sentence per
   * count. `{n}` is that scope's own count — the same provenance disclosure `CS-OMISSION-SCOPE` carries, in its
   * grouped form (C2b; the T-COUNT-1 allowance extends to it and to nothing else); `{domains}` is the members'
   * registered `CS-DOMAIN-*` labels joined with ", " and " and ". It says exactly what `CS-NOT-SHOWN` +
   * `CS-NOT-SHOWN-EXTENDED` say — omission is not absence, and it is not a statement about the applicant.
   */
  'CS-OVERVIEW-NOT-SHOWN-GROUP':
    'Not shown in this CV — across all {n} assessed entries, the CV does not document evidence in {domains}; that ' +
    'may be because those experiences are not described here or are not part of your path, and it is not a ' +
    'statement about you.',

  /*
   * A.5-P.18 — THE §6b SIGNALS RE-EXTRACTION STRINGS (live gap after Family Medicine activation, 2026-08-23;
   * provisional — owner may amend, like every other A.5-P row).
   *
   * WHY THEY EXIST. The read envelope has always carried a `signals_reextraction` offer when a CV's ledger holds
   * no `specialty_signal` facts of the active set (`reason: 'never_extracted' | 'set_changed'`), and every §6b row
   * on such a read renders `CS-CONV-UNBOUND` — correct, but permanent, because no client ever took the offer. The
   * surface now takes it automatically, once per CV and set, and these two strings are the only text that adds:
   *   * `CS-CONV-REEXTRACT-WAIT` — the announced wait while the pass runs, in the convention panel's own slot
   *     (`role="status" aria-busy`). `CS-EXTRACTION-LOADING` ("Reading your CV to build your review.") describes a
   *     different operation — this one does not rebuild the review, it reads the CV for this specialty's areas —
   *     so it gets its own honest sentence rather than borrowing a false one.
   *   * `CS-CONV-REEXTRACT-CTA` — the retry control shown ONLY after a failed attempt. It names the operation in
   *     the applicant's terms ("the specialty areas", not "signal set version drift").
   * Neither carries a band, a count, a comparative, or any claim about the applicant (§4.7, §8.5, §12.2).
   */
  'CS-CONV-REEXTRACT-WAIT': "Reading your entries for this specialty's areas — this takes a moment.",
  'CS-CONV-REEXTRACT-CTA': 'Add the specialty areas to this review',

  /*
   * A.5-P.20 — THE §6b SYNTHESIZED VERDICT AND ITS DISCLOSURE (owner directive 2026-08-23, verbatim: "giving users
   * feedback on dimensions will not help but rather overwhelm them. their care is: is the cv okay or not, where is
   * the problem, what to do/how to fix it. the specialty question is: does this cv reflect a family medicine cv?
   * decide extent/order based on dimension coverage, but raw data is not helpful as the synthesis is."; provisional
   * — owner may amend).
   *
   * WHAT CHANGED AND WHY. The panel led with a claim, a hedge, and then one row per ratified signal — 43 of them
   * for Family Medicine. That is the raw per-dimension data the owner says does not help. These strings are the
   * SYNTHESIS that now leads instead: one sentence answering the question the applicant actually asked, selected
   * by a TEMPLATE OVER FACTS (the row statuses the panel already computed), never generated.
   *
   * DOCUMENTATION-SCOPED, LIKE EVERY OTHER STRING ON THIS SURFACE. The subject of every sentence is the CV, never
   * the applicant: "reads as a documented {specialty} CV" is a statement about what the document says. There is no
   * fit, no ranking, no prediction, no score, no count and no fraction — `scanConventionText` enforces the
   * vocabulary half of that, and none of these ids is allowlisted, so they are held to the bare bans.
   *
   *   * `CS-CONV-VERDICT-ALL`      — every renderable row is `documented`.
   *   * `CS-CONV-VERDICT-MIXED`    — at least one `documented` AND at least one `partial`/`not_documented`.
   *   * `CS-CONV-VERDICT-NONE`     — no `documented` row, and at least one `partial`/`not_documented`. Carries the
   *     §4.7 omission-is-not-absence sentence, because "does not yet read as" is the strongest thing this layer
   *     ever says and it must not be readable as a claim about the person.
   *   * `CS-CONV-VERDICT-UNREAD`   — every renderable row is `unbound` (this version cannot read the areas from the
   *     CV's recorded facts). No verdict is honest there, so the string says so rather than defaulting to NONE.
   *   * `CS-CONV-TODO-HEADING`     — the heading of the problem → what-to-do list. Names the DOCUMENT's silence
   *     ("what your CV does not yet describe"), never a gap, a weakness, or a priority.
   *   * `CS-CONV-SHOW-AREAS`       — the disclosure that holds the full per-area list. The rows themselves are
   *     unchanged; only their default visibility moved.
   */
  'CS-CONV-VERDICT-ALL':
    'Your CV reads as a documented {specialty} CV: it describes work in every area these standards name.',
  'CS-CONV-VERDICT-MIXED':
    'Your CV reads as a documented {specialty} CV in some of the areas these standards name. In the areas below it '
    + 'does not yet describe what you did — add that where the experience exists.',
  'CS-CONV-VERDICT-NONE':
    'Your CV does not yet read as a documented {specialty} CV: it does not describe work in the areas these '
    + 'standards name. That may be because those experiences are not written here, or are not part of your path, '
    + 'and it is not a statement about you.',
  'CS-CONV-VERDICT-UNREAD':
    "This version cannot read these areas from your CV's recorded facts, so there is nothing to summarise for this "
    + 'specialty yet. The areas are listed below so you can check them yourself.',
  /*
   * A.5-P.23 — `CS-CONV-VERDICT-PARTIAL-READ` (Sol review, 2026-08-23). THE EXHAUSTIVE-CLAIM GUARD.
   *
   * `-ALL` says the CV describes work in EVERY area these standards name, and `-NONE` says it describes work in
   * NONE of them. Both are claims about the WHOLE ratified set. The tally that selects them ignores `unbound`
   * rows — areas this version could not read from the ledger — so one documented row beside forty-two unbound
   * ones selected `-ALL` and told the applicant their CV covered every area. That is a false statement about
   * their CV, which is the one thing this layer exists not to make.
   *
   * So whenever ANY row is unbound and anything at all was read, this template fires instead of the exhaustive
   * pair. It states the limitation and makes no claim in either direction — the rows the version COULD read are
   * still listed below it, so nothing is hidden. `-MIXED` is not reused: it says "in some of the areas… in the
   * areas below it does not yet describe what you did", which asserts there ARE such areas.
   */
  'CS-CONV-VERDICT-PARTIAL-READ': 'This version could not read every area these standards name from your CV, so it cannot yet summarise how your CV reads for {specialty}. The areas it could check are below.',
  'CS-CONV-TODO-HEADING': 'What your CV does not yet describe',
  'CS-CONV-SHOW-AREAS': 'Show the areas we checked',

  /*
   * A.5-P.21 — THE L1 OPENING'S TWO-SENTENCE PRIORITY SUMMARY (same owner directive, applied to the other half of
   * the surface: "this also applies to the other aspects of the evaluation"; provisional — owner may amend).
   *
   * The opening line used to serialize the review confidence, ELEVEN domain / presentation band sentences and
   * their §4.6 lines into one paragraph. Sol: "serialized output: repeated boilerplate plus eleven label/value
   * fields in prose." The opening line is now exactly TWO sentences — the CV-level confidence (`CS-REVIEW-CONF-*`,
   * A.5-P.15, unchanged) plus ONE of these, which says where to start. The band sentences keep their registered
   * templates and their builders (`domainBandsLine` / `presentationLine`) and move behind the disclosure
   * `CS-OVERVIEW-DETAIL-TOGGLE`; nothing about their wording changed.
   *
   *   * `CS-OVERVIEW-PRIORITY-START` — at least one section of the CV has a point to check.
   *   * `CS-OVERVIEW-PRIORITY-CLEAR` — no entry of any section raised one. States the absence of prompts, and
   *     claims nothing about the entries (§4.7, §12.2) — the same rule `CS-NOTHING-FLAGGED` follows.
   *   * `CS-OVERVIEW-DETAIL-TOGGLE`  — the disclosure the band sentences now sit behind.
   * None carries a count, a total, a band or a comparative.
   */
  'CS-OVERVIEW-PRIORITY-START':
    'Below is your CV, section by section — start at the top and open the first section that lists points to check.',
  'CS-OVERVIEW-PRIORITY-CLEAR':
    'Below is your CV, section by section — no entry raised a point to check.',
  'CS-OVERVIEW-DETAIL-TOGGLE': 'Show what this review read in each area',

  /*
   * A.5-P.22 — THE TWO PROVENANCE DISCLOSURE LABELS (owner directive 2026-08-23, verbatim: "some things seem like
   * you talking to yourself, not the user. display what is relevant to the user and not how we derived it or the
   * source"; provisional — owner may amend, like every other A.5-P row).
   *
   * NOTHING IS DELETED BY THESE TWO IDS AND NO CLAIM IS WEAKENED. §12.6's contract is about what this product may
   * never CLAIM, not about how many times a disclaimer is repeated on one screen; every allowlisted §6b string
   * still renders, still through `Copy`, still scanned. What changes is DEFAULT VISIBILITY: the derivation moves
   * behind a disclosure the reader opens, instead of standing between them and their own CV.
   *
   *   * `CS-CONV-SHOW-SOURCE` — the PER-ROW disclosure. `CS-CONV-SOURCE` ("Source: {source}, {pinpoint} ({date})")
   *     and the closed-world `CS-OMISSION-SCOPE` used to render inline on every single convention card, so a
   *     43-row specialty printed 43 citations and 43 scope sentences at somebody who asked what to fix. Both now
   *     render INSIDE this one `<details>`, per row, unchanged in wording.
   *   * `CS-CONV-ABOUT-TOGGLE`  — the PANEL-LEVEL disclosure. The mandatory claim (`CS-CONV-CLAIM`) and the
   *     mandatory footer (`CS-CONV-NOT-A-WEIGHT`) are both methodology: what this layer is and what it is not.
   *     They keep their spec order — claim first, footer last — INSIDE this disclosure, which sits at the foot of
   *     the panel. Neither string is edited and neither stops being reachable.
   * Both are labels for a control. Neither carries a band, a count, a comparative or a claim about the applicant.
   */
  'CS-CONV-SHOW-SOURCE': 'Show the standard and what was checked',
  'CS-CONV-ABOUT-TOGGLE': 'About this comparison',

  /*
   * A.5-P.24 — THE CONFIRMATION-STATE CHIP THAT SAID THE WRONG THING (owner verdict on a live read, 2026-08-23,
   * verbatim: "state semantics still wrong — fix for real"; provisional — owner may amend).
   *
   * WHAT WENT WRONG. §2.8's `extracted` state means "the APPLICANT has not acted on these facts". The ratified
   * chip for it, `CS-FACT-UNCONFIRMED`, reads "Not yet reviewed" — and on a CV the engine reviewed minutes ago,
   * rendered on the finished review surface, that sentence states something FALSE about the product's own work.
   * The applicant reads it as "CVCraft has not reviewed this entry yet". §2.1 rule 2 forbids a rendered string
   * that misdescribes the state it names, and the state here is a CONFIRMATION state, never a review state.
   *
   * WHY A NEW ID RATHER THAN AN EDIT. `CS-FACT-UNCONFIRMED` is an Appendix A.5 RATIFIED row (the 28-string R5
   * package, byte-identical). This registers its replacement provisionally and leaves the ratified row registered
   * and unedited, exactly as `CS-CONF-MODERATE-ENTRY` (A.5-P.12) did for the ratified `CS-CONF-MODERATE`: one
   * id→id substitution, at one call site, from a ratified id to a registered one — never to free text.
   *
   * WHAT IT SAYS INSTEAD: who has not acted (you), on what (these facts), and nothing about whether the review
   * ran. It is second person, like the rest of the surface, and it characterizes nobody.
   */
  'CS-FACT-UNCONFIRMED-ENTRY': "You haven't confirmed these facts yet",
  /*
   * A.5-P.25 — `CS-Q-DATE-END`, the DATES QUESTION NARROWED TO THE HALF THE ENTRY DOES NOT ANSWER.
   *
   * Owner, on a real CV, 2026-08-23: "Associate Physician role has 2023 attached. it should recognize that and
   * ask for an end date if needed." Asking "When did this take place?" of an entry that already states its start
   * year asks for something the entry answers, and it reads as not having looked — the same class of complaint as
   * the metric asks. So `CS-Q-DATES` is now only for an entry with NO dates at all; an entry stating a start and
   * nothing else gets this; an entry stating a range, or a start plus ongoing, is asked nothing.
   *
   * It is not a second question and it does NOT take a fourth `ProfileQuestionId`: no Canadian-medical-CV
   * convention expects "when did this start" without also expecting "and when did it end", so both shapes ride
   * the ONE `CS-Q-DATES` profile allowance and no profile row can drift between them. Its second sentence states
   * what the entry DOES say, which is the whole point of asking narrowly.
   */
  'CS-Q-DATE-END': 'Your entry states one date. If this ran over a period, adding when it ended — or that it is ongoing — makes that clear.',
} as const;

export const COPY = {
  /* §1.3 — intended use. ALLOWLISTED under §12.6 part 2; the sole permitted "candidate score"/"match outcomes". */
  'CS-INTENDED-USE':
    'CVCraft reviews how well your CV documents your experiences for your stated target. It does not score you ' +
    'as a candidate and does not predict interviews or match outcomes. Programs make those decisions using the ' +
    'full application and their own criteria.',
  'CS-INTENDED-USE-SHORT': 'Documentation review only — not a candidate score, not a match prediction.',

  /* §1.4 — input surface */
  'CS-INPUT-OTHER-RESUME':
    'This does not appear to be a CaRMS custom CV. The review below still describes what your document shows, ' +
    'but formatting advice may not apply.',
  'CS-INPUT-PACKAGE-DECLINE':
    'CVCraft reviews a CV. This looks like a full application package — upload the CV portion only.',

  /* §1.6 E3 — non-documentable criteria (R4 surface; pinned here so Appendix A stays complete) */
  'CS-NON-DOCUMENTABLE-CRITERIA':
    "Some of this program's published criteria — things like eligibility, reference letters, interviews, and " +
    'personal qualities — are not things a CV documents. They are not included in this review.',

  /* §3.0 — the mandatory facet framing. MUST accompany every facet display. */
  'CS-FACET-FRAMING':
    'These levels describe what your CV states, in increasing detail about your relationship to the work. ' +
    'No level is better than another — a clearly documented participatory role is fully documented.',

  /* §3.5 S6 — observership framing. Never "lacks hands-on responsibility". */
  'CS-OBSERVERSHIP':
    'This entry is documented as observership, which describes observation rather than delegated responsibility ' +
    '— that is what an observership is.',

  /* §4.6 — saturation and the documentation-not-volume message */
  'CS-SATURATION':
    'This domain has enough evidence — additional entries are unlikely to change how it reads. Effort is better ' +
    'spent elsewhere.',
  'CS-WHAT-WOULD-CHANGE': 'What would change this: stating what your role was on the {x} entry. Not more entries.',

  /* §4.7 — "Not shown". The single most important copy rule in the codebook: omission is not absence. */
  'CS-NOT-SHOWN': 'Not shown — this CV does not document evidence in this domain.',
  'CS-NOT-SHOWN-EXTENDED':
    "That may be because the experience isn't described here, or because it isn't part of your path. It is not a " +
    'statement about you.',

  /* §4.8 — CanMEDS is labelled vocabulary only, never an assessment axis */
  'CS-CANMEDS-CONVENTION':
    'Convention note: Programs often describe these areas using CanMEDS role language. Your {domain} evidence is ' +
    'what many programs would file under "{canmeds_role}". This is a vocabulary mapping, not a competency ' +
    'assessment.',

  /* §5.1 — scope is descriptive, never a ladder */
  'CS-SCOPE-DESCRIPTIVE':
    'Scope is descriptive. Wider is not better — deep local work and national work document different things.',

  /* §6.3 — overlay prevalence templates (R4 consumes these; pinned here for Appendix A completeness) */
  'CS-PREVALENCE-MOST':
    '{criterion} is explicitly named by most programs in this specialty ({n} of {N} program descriptions).',
  'CS-PREVALENCE-SOME':
    '{criterion} is explicitly named by some programs in this specialty ({n} of {N} program descriptions).',
  'CS-PREVALENCE-FEW':
    '{criterion} is explicitly named by a few programs in this specialty ({n} of {N} program descriptions).',
  'CS-PREVALENCE-NONE':
    "{criterion} is not explicitly named in this specialty's published program descriptions. That means it is " +
    'unknown, not that programs do not consider it.',

  /* §6.3 — overlay documentation templates */
  'CS-DOC-FULL': 'Your CV documents this on {n} entries.',
  'CS-DOC-PARTIAL': 'Your CV documents this partially: {reason}.',
  'CS-DOC-NONE': 'Your CV does not document this.',

  /* §6.4 — overlay hedges */
  'CS-OVERLAY-HEDGE':
    'Published program descriptions state criteria, not how programs weigh them. This is not a prediction.',
  'CS-MAPPING-UNCERTAIN':
    'The published wording for this criterion is general, so what counts as documenting it is uncertain.',

  /*
   * §6.6 — cycle expiry.
   *
   * TEXT AMENDED BY THE CODEBOOK v2.0 RATIFICATION (Appendix A.5, R5 §11 item 3). The id is pre-existing — it has
   * carried an Appendix A.1 row (a PURPOSE row: "§6.6 | Cycle-expiry fallback") since v1.x, with no exact text
   * pinned anywhere — and A.5 is the first place the codebook registers its EXACT copy. The module's prior text
   * ("Specialty profile not yet available for this cycle — generic review only.") was therefore never a ratified
   * string; A.5's row is. The ratified text is used here, so the one registry has one text per id.
   */
  'CS-PROFILE-EXPIRED':
    'The specialty criteria previously shown for this specialty have expired and are no longer applied.',

  /*
   * §7.4 — split output. The codebook's §7.4 block is an EXEMPLAR rendering whose second paragraph is specific to
   * that example's entries; the pinnable part is the two-statement shape that MUST NOT be merged into one verdict.
   * That shape is what this id carries. A Limited presentation band never lowers a content band, or vice versa
   * (T-SPLIT-1), which is enforced in `presentationBand.ts` by construction, not by this string.
   */
  'CS-SPLIT-OUTPUT': '{domain}: {content_band}. Presentation: {presentation_band}.',

  /* §8.2 — the flag taxonomy. Exactly 8 ids (Appendix A.1a), one per flag_type. */
  'CS-FLAG-STATUS-NOT-STATED':
    "The status is not stated clearly. This entry doesn't say whether the work is published, in press, " +
    'submitted, or presented. Adding the status makes the entry easier to read.',
  'CS-FLAG-DATES-INCONSISTENT':
    'Dates appear inconsistent. This entry shows {x} and {y}. If both are right, a short note would explain it; ' +
    'if one is a typo, this is a quick fix.',
  'CS-FLAG-CONTRIBUTION-NOT-STATED':
    'Contribution is not stated. This entry describes the project but not what you were responsible for.',
  'CS-FLAG-CLINICAL-ROLE-CONTEXT-NOT-STATED':
    "Clinical context isn't stated. This entry describes independent practice but doesn't state your licence " +
    'status or training stage at the time. Adding that context makes the entry self-explanatory.',
  'CS-FLAG-POSSIBLE-SENSITIVE-INFORMATION':
    "Possible patient or sensitive information. This entry includes {detail_type}. Consider removing it — it isn't " +
    "needed for the application, and patient details shouldn't appear in a CV.",
  'CS-FLAG-POSSIBLE-PERSONAL-INFORMATION':
    "Personal detail that isn't needed. This entry includes {detail_type}. Consider removing it — it isn't needed " +
    'for the application, and it may introduce bias.',
  'CS-FLAG-DUPLICATE-LOOKING-ITEMS':
    'These two entries may be the same work at different stages. They differ on {field}. If they\'re stages of ' +
    "one project — an abstract, then a poster, then a paper — listing them is fine; it would just help to make " +
    "the {field} consistent. If they're separate works, a small distinguishing detail would help.",
  'CS-FLAG-EXTRACTION-UNCERTAIN':
    'We had trouble reading this entry. Please check the facts we pulled from it — you can edit anything ' +
    "that's wrong.",

  /* §8.4 — escalation. Descriptive, never characterizing, even after user confirmation. */
  'CS-FLAG-ESCALATED':
    "You marked this item as needing correction. It's excluded from the review until you update it.",

  /* §8.2 / §10.3 — questions to answer. NOT flags: there is nothing to clarify, the information is not there. */
  'CS-Q-DATES': 'When did this take place? Adding dates helps a reader place this entry.',
  'CS-Q-DURATION': 'How long or how often was this? A duration or frequency makes the entry easier to read.',
  'CS-Q-CLINICAL-CONTEXT':
    'What was your training stage or licence status at the time? This is optional, and it makes the entry ' +
    'self-explanatory.',

  /* §9.2 — confidence copy. Describes how well we could READ the document, never the applicant. */
  'CS-CONF-HIGH': 'We read this entry clearly.',
  'CS-CONF-MODERATE': "Some of this entry wasn't clear to read — check the facts below.",
  'CS-CONF-LOW':
    'We had difficulty reading this. Please review and correct the extracted facts before relying on this section.',

  /*
   * §10.2 — `CS-DEADLINE-SUPPRESSION` REMOVED IN CODEBOOK v1.7, with Group B and the deadline suppressor. Its
   * only function was to explain why an experience-building group had been hidden; with no such group there is
   * nothing to explain, and Appendix A now states it MUST NOT be reintroduced.
   */

  /*
   * §2.7 / §12.1 — the SOLE allowlisted total-entry-count string (T-COUNT-1). It renders only inside the
   * closed-world evidence disclosure attached to an omission claim, never in a headline, band label, summary, PDF
   * header, or telemetry label. The number is a provenance disclosure about the assessment's scope, not a measure
   * of the applicant — without it C2b's traceability requirement cannot be met.
   */
  'CS-OMISSION-SCOPE': 'assessed across all {n} entries in your CV',

  /* Appendix A.2 — domain names (§4.1). Fixed display order, NEVER re-ordered by band (T-ORDER-1). */
  'CS-DOMAIN-CLINICAL': 'Clinical experience & documented responsibility',
  'CS-DOMAIN-LEADERSHIP': 'Leadership & project ownership',
  'CS-DOMAIN-SCHOLARSHIP': 'Scholarship, inquiry & QI',
  'CS-DOMAIN-SERVICE': 'Service & advocacy',
  'CS-DOMAIN-TEACHING': 'Teaching & mentorship',

  /* §5.7 — the collaboration descriptor (demoted from a domain by OQ-6). Never banded, never a gap. */
  'CS-DESC-COLLABORATION': 'Documented collaborative function',

  /* §4.2 — content band labels. The top band is "Substantial"; superlatives are non-negotiably banned. */
  'CS-BAND-NOT-SHOWN': 'Not shown',
  'CS-BAND-LIMITED': 'Limited',
  'CS-BAND-DEMONSTRATED': 'Demonstrated',
  'CS-BAND-SUBSTANTIAL': 'Substantial',

  /* §7.2 — presentation band labels. A DIFFERENT ladder whose bottom level is an availability state. */
  'CS-PBAND-NOT-ASSESSABLE': 'Not assessable',
  'CS-PBAND-LIMITED': 'Limited',
  'CS-PBAND-ADEQUATE': 'Adequate',
  'CS-PBAND-STRONG': 'Strong',

  /* §7.1 — presentation sub-element names */
  'CS-PSUB-NAVIGABILITY': 'Navigability',
  'CS-PSUB-CHRONOLOGY': 'Chronology',
  'CS-PSUB-STATUS-LABELLING': 'Status labelling',
  'CS-PSUB-ATTRIBUTION': 'Attribution clarity',
  'CS-PSUB-CONSISTENCY': 'Consistency',

  /* §3.1–3.3 — facet names and level labels */
  'CS-FACET-RESPONSIBILITY': 'Responsibility',
  'CS-FACET-COMPLETION': 'Completion',
  'CS-FACET-CONTINUITY': 'Continuity',
  'CS-RESP-L1': 'Role not stated',
  'CS-RESP-L2': 'Stated participation',
  'CS-RESP-L3': 'Stated defined responsibility',
  'CS-RESP-L4': 'Stated direction of others or of the whole',
  'CS-COMP-L1': 'Status not stated',
  'CS-COMP-L2': 'Stated activity under way',
  'CS-COMP-L3': 'Stated completed deliverable',
  'CS-COMP-L4': 'Stated dissemination, use, or recognition',
  // RENAMED IN CODEBOOK v1.8 (amendment A), id deliberately unchanged. The old text — "Duration not stated /
  // single occasion" — asserted that the CV stated no duration, which is FALSE on the entry that made the rename
  // necessary: a stated sub-12-month span with no stated frequency bands here, and it plainly states a duration.
  // §2.1 rule 2 forbids a rendered string that misdescribes the document. The new text names the engine's
  // evidentiary conclusion — a repeated dated span was not established — and asserts nothing about the CV.
  'CS-CONT-L1': 'Single occasion / repeated dated span not established',
  'CS-CONT-L2': 'Stated repeated involvement, under 12 months',
  'CS-CONT-L3': 'Stated involvement, 12 months or more',
  'CS-CONT-L4': 'Stated change in role over time',

  /* §6.7 — the ONE permitted program-mode summary line (R4/R6; pinned for Appendix A completeness) */
  'CS-PROG-SUMMARY':
    'Required criteria: {r_doc} of {r_total} documented. Preferred criteria: {p_doc} of {p_total} documented, ' +
    '{p_partial} partial, {p_none} not shown.',

  /*
   * §10.2 group heading and §10.3 kind labels.
   *
   * There is exactly ONE group heading. `CS-REC-GROUP-B` ("Experience-building, months out") was REMOVED in
   * codebook v1.7: §10.2's mandate for that group is not jointly satisfiable with §10.5's bans, §4.6's
   * documentation-not-volume requirement, §11.5, or §1.1's construct, so the group is deleted rather than shipped
   * permanently empty. Appendix A states it MUST NOT be reintroduced.
   */
  'CS-REC-GROUP-A': 'Documentation changes you can make today',
  'CS-REC-KIND-KNOWN': 'Known fact',
  'CS-REC-KIND-QUESTION': 'Question to answer',
  'CS-REC-KIND-PHRASING': 'Suggested phrasing',

  /* ---------------------------------------------------------------------------------------------------------- *
   * Appendix A.5 — THE 28 R5 REVIEW-SURFACE STRINGS (codebook v2.0, ratified 2026-08-15 as the R5 §11 package).
   *
   * These are the ids the R5 review surface renders "and no others of its own". They are registered HERE, beside
   * every other Appendix A id, for the same reason the `RT-*` templates were moved here in v1.7: T-COPY-1's rule
   * is that the registered id set is computable from ONE module, and a second registry is a second thing to drift.
   *
   * `CS-PROFILE-EXPIRED` is an A.5 row too, but it is an id that already existed above (§6.6), so it stays at its
   * original position rather than being duplicated here — its text is the A.5 text.
   * ---------------------------------------------------------------------------------------------------------- */

  /* The labelled formative beta (§8.3), pinned adjacent to `CS-INTENDED-USE-SHORT`. */
  'CS-BETA-LABEL': 'Formative beta',

  /* §4.1 — the evidence affordance. Every extracted fact can show the applicant their own words back. */
  'CS-SHOW-SOURCE': 'Shown from your CV',

  /* §2.8 confirmation-state chips (R5.3's fact-ledger panel; registered now so the id set is complete). */
  'CS-FACT-UNCONFIRMED': 'Not yet reviewed',
  'CS-FACT-CONFIRMED': 'Confirmed',
  'CS-FACT-EDITED': 'Edited by you',
  'CS-FACT-FLAGGED': 'Flagged as incorrect',

  /* §5 correction-loop affordances (R5.3). */
  'CS-CORRECT-ACTION': 'Edit',
  'CS-CONFIRM-ENTRY-CTA': 'Looks right — run the review',
  'CS-FLAG-WRONG-ACTION': "This isn't right",
  'CS-FLAG-WRONG-UNDO': 'Undo — this was correct',
  'CS-DIFF-A11Y': '{domain} changed from {before_band} to {after_band}.',

  /* Freshness envelope (§1.2c states S and X) and its affordances. */
  'CS-STALE-VERSION':
    'This review no longer reflects the current inputs. Refresh to see it under the current inputs.',
  'CS-REFRESH-VERSION-CTA': 'Refresh review',
  'CS-REFRESH-DISABLED': "Refresh isn't available for this review right now.",

  /* Legacy (§8.4) — a stored previous-format read, honoured read-only. */
  'CS-LEGACY-BANNER': 'Reviewed under our previous format.',
  'CS-LEGACY-ABSENT': 'No previous-format review is on file for this CV.',

  /*
   * Corrupt (state X). SPLIT DELIBERATELY: the first names the action available WITHOUT guaranteeing its outcome
   * (a valid ledger exists, so `/refresh` will succeed); the second is rendered only when the recovery path
   * reports `corrupt_unrecoverable_no_ledger`, and offers no retry affordance because none exists.
   */
  'CS-READ-CORRUPT': "This review couldn't be loaded. Try Refresh review.",
  'CS-READ-CORRUPT-UNRECOVERABLE':
    "This review couldn't be loaded and can't be regenerated automatically. Contact support.",

  /* Transport/empty states. */
  'CS-ERR-READ-UNAVAILABLE': "The review isn't available right now. Try again in a moment.",
  'CS-EMPTY-NO-READ': 'No review yet. Import your CV to generate one.',
  'CS-UPGRADE-CTA': 'Generate a review under the current format',

  /* §4.5 — overlay source reference. Names the row's OWN pinned release, never "the active release". */
  'CS-OVERLAY-SOURCE': 'Source: {profile_release_id}',
  'CS-GENERIC-MODE-NOTICE': 'This review uses the general rubric. No specialty-specific criteria are applied.',
  /* R7.3 — Sol's ratified framing. Renders INSTEAD OF `CS-GENERIC-MODE-NOTICE` when the §6b convention panel
     directly above has ≥1 renderable row: the flat "no specialty-specific criteria are applied" claim directly
     contradicted rows the convention panel had just rendered. `CS-GENERIC-MODE-NOTICE` itself is untouched and
     keeps rendering for the genuinely-generic case (no convention rows at all). */
  'CS-CONVENTION-SCOPE-NOTICE':
    'Your CV is reviewed with a general documentation rubric. The specialty overlay above separately shows where your CV explicitly documents areas found in the cited specialty training standards; it does not model program selection.',

  /* §8.3 — the flag-off cohort, choosing by whether a legacy read actually exists. */
  'CS-RUBRIC-DISABLED': "This review format isn't enabled for your account yet. Showing your previous-format review.",
  'CS-RUBRIC-DISABLED-NO-LEGACY':
    "This review format isn't enabled for your account yet, and no previous-format review is on file for this CV.",

  /* §5 — editing states (R5.3). */
  'CS-EDIT-DISABLED': "Editing isn't available while this review is loading.",
  'CS-CORRECT-CONFLICT': 'This review changed since you opened it.',

  /* Appendix A.5-P — the PROVISIONAL block, spread in from its own const above so that its id set stays
     separately enumerable (`PROVISIONAL_COPY_STRING_IDS`) and the ratified "exactly 28" A.5 rows above stay
     byte-identical and countable on their own. */
  ...A5P_COPY,
  /* Appendix A.6 — the R7 §6b specialty convention layer strings, registered by R7.1. Spread from their own const
     so the §6b id set stays separately enumerable (`CONVENTION_COPY_STRING_IDS`) and the A.5-P count above stays
     countable on its own. */
  ...A6_COPY,
} as const;

export type CopyStringId = keyof typeof COPY;

/* ------------------------------------------------------------------------------------------------------------ *
 * Appendix A.3 / A.4 — SUGGESTED-PHRASING TEMPLATES AND THE CLOSED PLACEHOLDER SET (codebook v1.7)
 *
 * WHY THESE LIVE HERE AND NOT IN `recommendations.ts`. Template text and placeholder literals are RENDERED USER-
 * FACING TEXT, so T-COPY-1's rule — every rendered string resolves to an id in Appendix A — governs them exactly
 * as it governs a copy string. Through v1.6 the `RT-*` family was pinned in `recommendations.ts` and registered in
 * Appendix A nowhere, so the copy contract could not see it: the R3 review found T-COPY deriving its id set from
 * `COPY` and therefore missing the templates entirely. Registering them beside `COPY` is what lets the test parse
 * Appendix A out of the codebook and compare it against a single module.
 * ------------------------------------------------------------------------------------------------------------ */

/**
 * Appendix A.3. Every template is a RESTATEMENT SCAFFOLD: it re-renders facts the ledger already holds, in a
 * clearer order. None introduces a fact, an adjective of magnitude, or a quantifier (AF1), and none asks for
 * quantification the applicant has not supplied (AF4). Adding one is an owner ratification, not a code change.
 */
export const PHRASING_TEMPLATES = {
  'RT-ROLE-ON-ENTRY-V1': '{title} ({dates}) — {role}.',
  'RT-ROLE-AND-STATUS-V1': '{title} ({dates}) — {role}. {status}.',
} as const;

export type PhrasingTemplateId = keyof typeof PHRASING_TEMPLATES;

/**
 * Appendix A.4 — the CLOSED set of literal placeholders a slot may carry.
 *
 * The placeholder is not a degraded fallback; it is the mechanism by which the anti-fabrication moat stays honest.
 * "[your role here]" says plainly that we do not know the role, where inventing a plausible one would be an AF1
 * violation. But the set must be CLOSED, and through v1.6 it was not: `checkPhrasingRenderable` accepted any
 * string as a "placeholder", so an arbitrary fabricated sentence passed the AF-2 check by declaring itself one.
 */
export const APPROVED_PLACEHOLDERS = {
  'PL-ROLE': '[your role here]',
  'PL-PARTICIPANT-COUNT': '[number of participants]',
  'PL-DATES': '[dates here]',
  'PL-STATUS': '[status here]',
} as const;

export type PlaceholderId = keyof typeof APPROVED_PLACEHOLDERS;

/** The literals themselves, for the AF-2 membership test. */
export const APPROVED_PLACEHOLDER_LITERALS: readonly string[] = Object.values(APPROVED_PLACEHOLDERS);

/**
 * The closed, enumerated id set (T-COPY-1: "the registered id set is computable from Appendix A alone"). Derived
 * from `COPY` rather than written out a second time, so the two can never drift apart.
 */
export const COPY_STRING_IDS: readonly CopyStringId[] = Object.keys(COPY) as CopyStringId[];

/**
 * The Appendix A.5-P ids ONLY — **Claude+Sol adjudicated 2026-08-21 (170 accepted as drafted, 5 replaced per
 * Sol); owner may amend**.
 *
 * Exported so the owner digest, and the copy tests, can name exactly which rendered strings are awaiting
 * ratification without diffing two registries by hand. Derived from the block itself, never re-listed.
 */
/**
 * Appendix A.6 — the §6b convention-layer ids, derived from the block itself so the two cannot drift.
 *
 * NOT provisional (these were owner-ratified with §6b on 2026-08-22, before any code existed), so they are
 * deliberately NOT members of `PROVISIONAL_COPY_STRING_IDS`.
 */
export const CONVENTION_COPY_STRING_IDS: readonly CopyStringId[] =
  Object.keys(A6_COPY) as CopyStringId[];

/**
 * §12.6 — the §6b disclaimer allowlist (spec §4). These ids are the ONLY place "prediction", "weight",
 * "requirement" or "select" may appear on the convention surface, and only in the negated form registered above.
 */
export const CONVENTION_ALLOWLISTED_COPY_IDS: readonly CopyStringId[] = [
  'CS-CONV-CLAIM',
  'CS-CONV-HEDGE-CONVENTION',
  'CS-CONV-HEDGE-SURVEY',
  'CS-CONV-HEDGE-CORPUS',
  'CS-CONV-NOT-A-WEIGHT',
  'CS-CONV-EMPTY-CONVENTION',
  'CS-CONV-EMPTY-SURVEY',
  'CS-CONV-EMPTY-CORPUS',
];

export const PROVISIONAL_COPY_STRING_IDS: readonly CopyStringId[] =
  Object.keys(A5P_COPY) as CopyStringId[];

/**
 * The `CS-FLAG-<flag_type>` family (Appendix A.1a), expanded to EXACTLY the 8 §8.2 flag types.
 *
 * `CS-FLAG-ESCALATED` is deliberately NOT a member — it is a standalone id. The deleted flag types
 * `missing_explicit_requirement` (§1.6 E1) and `scope_of_practice_wording` (§3.5) have no ids and MUST NOT be
 * reintroduced into this expansion.
 */
export const FLAG_COPY_IDS = {
  status_not_stated: 'CS-FLAG-STATUS-NOT-STATED',
  dates_inconsistent: 'CS-FLAG-DATES-INCONSISTENT',
  contribution_not_stated: 'CS-FLAG-CONTRIBUTION-NOT-STATED',
  clinical_role_context_not_stated: 'CS-FLAG-CLINICAL-ROLE-CONTEXT-NOT-STATED',
  possible_sensitive_information: 'CS-FLAG-POSSIBLE-SENSITIVE-INFORMATION',
  possible_personal_information: 'CS-FLAG-POSSIBLE-PERSONAL-INFORMATION',
  duplicate_looking_items: 'CS-FLAG-DUPLICATE-LOOKING-ITEMS',
  extraction_uncertain: 'CS-FLAG-EXTRACTION-UNCERTAIN',
} as const satisfies Record<string, CopyStringId>;

export type FlagType = keyof typeof FLAG_COPY_IDS;

/**
 * Fill a template's `{slot}` placeholders. Unfilled slots are left LITERAL rather than blanked: a template that
 * silently rendered "documents this on  entries" would state a fact nobody supplied. §10.3's suggested-phrasing
 * path additionally refuses to render at all on an unresolved slot (AF-2) — that check lives in
 * `recommendations.ts`, because it is about slot PROVENANCE, not about string substitution.
 */
export function fillTemplate(id: CopyStringId, slots: Readonly<Record<string, string>>): string {
  let out: string = COPY[id];
  for (const [k, v] of Object.entries(slots)) out = out.split(`{${k}}`).join(v);
  return out;
}
